/**
 * A página do link: escolher a senha nova sem precisar entrar.
 *
 * É a única tela do painel que **funciona sem sessão**, e tem que ser: quem está aqui é
 * exatamente quem não consegue entrar. O que faz ela ser segura não é uma sessão, é o token que
 * veio no endereço, que vale quinze minutos e serve uma vez.
 *
 * Três decisões desta tela:
 *
 * **"Você é quem?" só aparece quando precisa.** Com uma pessoa só, perguntar seria obrigar
 * alguém a confirmar o próprio nome para seguir. Com duas, não perguntar seria trocar a senha de
 * quem a tela chutasse, e o chute erraria metade das vezes.
 *
 * **A senha é digitada duas vezes.** Ela não aparece na tela enquanto se digita, e quem erra uma
 * letra aqui fica trancado de novo e precisa pedir outro link. Dez segundos de digitar de novo
 * valem menos do que isso.
 *
 * **O endereço é limpo depois de trocar, e não antes.** Depois, o token já não serve para nada e
 * não tem por que ficar na barra de um computador emprestado. Antes seria pior: quem
 * recarregasse a página perderia o link sem entender por quê.
 */
import { useEffect, useState, type JSX } from 'react'
import { ErroDoServidor, servidor, type QuemOLinkAlcanca, type SenhaTrocadaPeloLink } from '../api'
import { NOME_DO_SISTEMA } from '../identidade'

type Estado =
  | { fase: 'abrindo' }
  /** O link não serve. A frase do servidor diz qual dos motivos é. */
  | { fase: 'nao-serve'; recado: string }
  | { fase: 'escolher'; quem: QuemOLinkAlcanca[] }
  | { fase: 'senha'; escolhido: QuemOLinkAlcanca; eraSozinho: boolean }
  | { fase: 'pronto'; feita: SenhaTrocadaPeloLink; criou: boolean }

/*
 * CRIAR OU TROCAR. Pedido da auditora, de 17/10/2026: o primeiro acesso chegava numa tela que
 * dizia "Trocar a senha" a quem nunca teve senha nenhuma. O servidor diz, por pessoa, se ela já
 * tem senha; servidor antigo, que não diz, continua com "Trocar".
 */
const criaSenha = (q: QuemOLinkAlcanca): boolean => q.ja_tem_senha === false
const tituloPara = (quem: QuemOLinkAlcanca[]): string =>
  quem.every(criaSenha) ? 'Criar a senha' : quem.some(criaSenha) ? 'Criar ou trocar a senha' : 'Trocar a senha'

export function TrocarASenha({ token }: { token: string }): JSX.Element {
  const [estado, setEstado] = useState<Estado>({ fase: 'abrindo' })
  const [senha, setSenha] = useState('')
  const [deNovo, setDeNovo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [trocando, setTrocando] = useState(false)

  useEffect(() => {
    let vale = true
    servidor
      .abrirOLink(token)
      .then((aberto) => {
        if (!vale) return
        const primeiro = aberto.quem[0]
        /*
         * Uma pessoa só: vai direto para a senha. É o caso de quase todo mundo, e um passo a
         * menos num caminho que a pessoa já está fazendo contrariada.
         */
        if (aberto.quem.length === 1 && primeiro) {
          setEstado({ fase: 'senha', escolhido: primeiro, eraSozinho: true })
        } else {
          setEstado({ fase: 'escolher', quem: aberto.quem })
        }
      })
      .catch((falha: unknown) => {
        if (!vale) return
        setEstado({
          fase: 'nao-serve',
          recado:
            falha instanceof ErroDoServidor
              ? falha.message
              : 'Não foi possível abrir este link. Confira a internet e tente de novo.'
        })
      })
    return () => {
      vale = false
    }
  }, [token])

  async function trocar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    if (estado.fase !== 'senha') return

    if (senha !== deNovo) {
      setErro('As duas senhas não são iguais. Digite a mesma senha nas duas caixas.')
      return
    }

    setErro(null)
    setTrocando(true)
    try {
      const feita = await servidor.trocarPeloLink(token, estado.escolhido.quem, senha)
      setSenha('')
      setDeNovo('')
      /*
       * O token já foi gasto: tirar ele da barra tira do histórico de um computador emprestado
       * um endereço que não serve mais, e deixa a página recarregável sem susto.
       */
      window.history.replaceState(null, '', '/')
      setEstado({ fase: 'pronto', feita, criou: criaSenha(estado.escolhido) })
    } catch (falha) {
      setErro(
        falha instanceof ErroDoServidor
          ? falha.message
          : 'Não foi possível trocar a senha. Tente de novo.'
      )
      // A senha não é limpa aqui: senha fraca se corrige, e apagar faria digitar tudo de novo.
    } finally {
      setTrocando(false)
    }
  }

  return (
    <main className="entrar" data-fase={estado.fase}>
      <h1>{NOME_DO_SISTEMA}</h1>
      <p className="subtitulo">
        {estado.fase === 'escolher'
          ? tituloPara(estado.quem)
          : estado.fase === 'senha'
            ? tituloPara([estado.escolhido])
            : estado.fase === 'pronto'
              ? (estado.criou ? 'Criar a senha' : 'Trocar a senha')
              : 'Criar ou trocar a senha'}
      </p>

      {estado.fase === 'abrindo' && <p className="recado aguarde">Abrindo o link...</p>}

      {estado.fase === 'nao-serve' && (
        <>
          <p className="recado" role="alert">{estado.recado}</p>
          <a className="como-link" href="/">Voltar para a tela de entrar</a>
        </>
      )}

      {estado.fase === 'escolher' && (
        <>
          {/*
            Duas pessoas com o mesmo e-mail é caso de verdade: marido e mulher na tesouraria com
            uma caixa de e-mail só. Cada uma troca a senha dela, e a trilha do servidor registra
            qual foi escolhida.
          */}
          <p className="o-que-fazer">
            Este e-mail está no cadastro de mais de uma pessoa. De quem é a senha que você quer
            {estado.quem.every(criaSenha) ? ' criar' : estado.quem.some(criaSenha) ? ' criar ou trocar' : ' trocar'}?
          </p>

          <ul className="escolher-quem">
            {estado.quem.map((q) => (
              <li key={q.quem}>
                <button
                  type="button"
                  className="principal"
                  onClick={() => {
                    setErro(null)
                    setEstado({ fase: 'senha', escolhido: q, eraSozinho: false })
                  }}
                >
                  {q.nome}
                </button>
                <span className="detalhe">
                  {criaSenha(q) ? 'Ainda não tem senha. Cria' : 'Troca'} {q.o_que_troca}.
                </span>
              </li>
            ))}
          </ul>

          <p className="rodape">
            Trocar a senha de uma não mexe na senha da outra.
          </p>
        </>
      )}

      {estado.fase === 'senha' && (
        <>
          <p className="o-que-fazer">
            {criaSenha(estado.escolhido) ? (
              estado.eraSozinho ? (
                <>Este é o seu primeiro acesso. Crie a sua senha: é com ela que você vai entrar daqui em diante.</>
              ) : (
                <>
                  Primeiro acesso de <strong>{estado.escolhido.nome}</strong>. Crie a senha.
                </>
              )
            ) : estado.eraSozinho ? (
              <>Escolha a sua senha nova. A senha de antes deixa de valer.</>
            ) : (
              <>
                Senha nova de <strong>{estado.escolhido.nome}</strong>. A senha de antes deixa de valer.
              </>
            )}
          </p>

          {erro !== null && <p className="recado" role="alert">{erro}</p>}

          <form onSubmit={trocar}>
            <label>
              {criaSenha(estado.escolhido) ? 'Senha' : 'Senha nova'}
              <input
                type="password"
                name="senha-nova"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="new-password"
                autoFocus
                required
              />
            </label>

            <label>
              {criaSenha(estado.escolhido) ? 'Digite a senha outra vez' : 'Digite a senha nova outra vez'}
              <input
                type="password"
                name="senha-de-novo"
                value={deNovo}
                onChange={(e) => setDeNovo(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>

            <button className="principal" type="submit" disabled={trocando}>
              {criaSenha(estado.escolhido)
                ? (trocando ? 'Criando...' : 'Criar a senha')
                : (trocando ? 'Trocando...' : 'Trocar a senha')}
            </button>
          </form>

          <p className="rodape">
            De 6 a 20 caracteres, com pelo menos uma letra e um número. É a mesma regra de
            sempre.
            <br />
            {criaSenha(estado.escolhido) ? 'Esta é' : 'Esta senha troca'} {estado.escolhido.o_que_troca}.
          </p>
        </>
      )}

      {estado.fase === 'pronto' && (
        <>
          <p className="recado feito" role="status">
            Pronto. A senha de {estado.feita.nome} foi {estado.criou ? 'criada' : 'trocada'}.
          </p>

          {/*
            O que fazer agora depende de onde a pessoa usa a senha.

            A frase do notebook é a que evita o telefonema: sem ela, a pessoa tenta no
            computador da tesouraria antes de ele sincronizar, não funciona, e conclui que nada
            disto serviu. Ela vem junto do "não há onde entrar" num parágrafo só, porque são a
            mesma explicação e separá-las fazia a tela repetir "computador da tesouraria" três
            vezes seguidas. O primeiro retrato mostrou isso.
          */}
          {estado.feita.vale_no_notebook && (
            <p className="explicacao">
              Esta é a senha do computador da tesouraria. Lá ela passa a valer na próxima vez que
              aquele computador se conectar à internet e receber as novidades.
              {!estado.feita.entra_no_painel && (
                <> Você não tem conta no painel, e por isso não há onde entrar por aqui.</>
              )}
            </p>
          )}

          {estado.feita.tem_segundo_fator && (
            <p className="explicacao atencao">
              A sua conta tem segundo fator. Ao entrar, o código do aplicativo do celular vai ser
              pedido como sempre: {estado.criou ? 'criar' : 'trocar'} a senha não mexeu nisso.
            </p>
          )}

          {estado.feita.entra_no_painel && (
            <a className="principal como-botao" href="/">Entrar no painel</a>
          )}
        </>
      )}
    </main>
  )
}
